import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { BadgeCheck, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export const VerificationRequest = () => {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [links, setLinks] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/verification/me").then(({ data }) => setState(data)).catch(() => {}); }, []);

  if (!state || state.verified) return null;
  const pending = state.request?.status === "pending";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/verification/request", { reason, links });
      setState((s) => ({ ...s, request: data }));
      setOpen(false);
      toast.success("Request sent — an admin will review it");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (pending) {
    return (
      <span data-testid="verification-pending" className="flex items-center gap-2 border border-[#F1A817]/40 text-[#F1A817] font-semibold text-sm px-4 py-2.5 rounded-lg">
        <Clock size={14} /> Verification pending
      </span>
    );
  }

  return (
    <>
      <button data-testid="request-verification-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 border border-white/10 text-[#9CA3AF] font-semibold text-sm px-4 py-2.5 rounded-lg hover:text-white hover:border-[#0066FF] transition-colors">
        <BadgeCheck size={14} className="text-[#0066FF]" /> Get verified
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="verification-dialog" className="bg-[#0F1117] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><BadgeCheck size={20} className="text-white" fill="#0066FF" /> Request verification</DialogTitle>
            <DialogDescription className="text-[#9CA3AF]">
              Verified archivists are trusted contributors. Tell us about your collection and why it matters.
              {state.request?.status === "denied" && <span data-testid="verification-denied-note" className="block mt-2 text-[#F1A817]">Your last request wasn't approved{state.request.note ? `: ${state.request.note}` : "."} You can apply again.</span>}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <textarea data-testid="verification-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={5} maxLength={1000} placeholder="What do you archive, how long have you collected, how do you source it…" className="w-full bg-[#161922] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#0066FF] resize-none" />
            <input data-testid="verification-links" value={links} onChange={(e) => setLinks(e.target.value)} maxLength={500} placeholder="Links to your other archives or socials (optional)" className="w-full bg-[#161922] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#0066FF]" />
            <p className="text-[11px] text-[#52525B]">{reason.trim().length < 10 ? `At least 10 characters (${reason.trim().length}/10)` : `${reason.length}/1000`}</p>
            <button data-testid="verification-submit" disabled={sending || reason.trim().length < 10} className="w-full flex items-center justify-center gap-2 bg-[#0066FF] hover:bg-[#0055CC] disabled:opacity-40 text-white font-semibold text-sm py-2.5 rounded-lg transition-colors">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <BadgeCheck size={15} />} Send request
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
